import cn from 'classnames';
import React, {forwardRef, useEffect, useRef, useState} from 'react';

import server from '../../services/server/server';
import helpers from '../../utils/helpers';
import Button from '../Button/Button';
import Image from '../Image/Image';
import AppLoaderMedia from '../Loader/AppLoader/AppLoaderMedia';
import styles from './Video.module.scss';

const Video = (props, ref) => {
    const {
        autoplay,
        className,
        controlsClassName,
        isControlsVisible = true,
        loop,
        muted,
        onCanPlay,
        onControlsClickCustom,
        onError,
        onLoad,
        playsinline,
        thumbnailSrc,
        src,
        children,
    } = props;

    const videoRef = useRef(ref);
    const [isLoaded, setIsLoaded] = useState(false);

    const [isVideoCanStart, setIsVideoCanStart] = useState(false);
    const [isAnimationFinished, setIsAnimationFinished] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    const onCanPlayFunc = () => {
        helpers.runFunction(onCanPlay);
        setIsVideoCanStart(true);
    };

    const onLoadFunc = () => {
        helpers.runFunction(onLoad);
    };

    const onErrorFunc = () => {
        helpers.runFunction(onError);
    };

    const onTransitionEnd = () => {
        setIsAnimationFinished(true);
    };

    const thumbnailClassName = cn(styles.Thumbnail, {[styles.Hidden]: isVideoCanStart}, className);
    const controlButtonWrapperClassName = cn(styles.ControlButtonWrapper, controlsClassName, {
        [styles.Visible]: isLoaded,
    });
    const controlButtonClassName = cn(styles.ControlButton, {
        [styles.Paused]: !isVideoPlaying,
    });

    useEffect(() => {
        if (isAnimationFinished) {
            if (autoplay) {
                videoRef.current.play?.();
            }

            onCanPlayFunc();
        }
    }, [isAnimationFinished]);

    useEffect(() => {
        server
            .get(src)
            .then(onLoadFunc)
            .catch(onErrorFunc)
            .finally(() => {
                setIsLoaded(true);
            });
    }, []);

    const onControlButtonClick = () => {
        const video = videoRef.current;

        if (onControlsClickCustom) {
            helpers.runFunction(onControlsClickCustom);
        } else {
            video.paused ? video.play() : video.pause();
        }
    };

    const onPlay = () => setIsVideoPlaying(true);
    const onPause = () => setIsVideoPlaying(false);

    return (
        <>
            <div className={styles.VideoContainer} aria-hidden='true'>
                {isLoaded ? (
                    <>
                        {children}
                        <video
                            autoPlay={autoplay}
                            className={className}
                            loop={loop}
                            muted={muted}
                            onCanPlay={onCanPlayFunc}
                            onPause={onPause}
                            onPlay={onPlay}
                            playsInline={playsinline}
                            ref={ref ? ref : videoRef}
                            src={src}
                        />
                        {isControlsVisible && (
                            <Button
                                className={controlButtonWrapperClassName}
                                onClick={onControlButtonClick}
                                tabIndex='-1'
                            >
                                <div className={controlButtonClassName} />
                            </Button>
                        )}
                    </>
                ) : (
                    <AppLoaderMedia loaderClassName={styles.Loader} />
                )}

                <div className={styles.VideoContainer} aria-hidden='true'>
                    <Image onTransitionEnd={onTransitionEnd} className={thumbnailClassName} src={thumbnailSrc} />
                </div>
            </div>
        </>
    );
};

export default forwardRef(Video);
